import { useContext, useState } from 'react';
import { QuantityDraftContext } from './quantityDraftContext';

/**
 * One line's quantity draft and save state. The draft is mirrored into
 * QuantityDraftProvider (CostCell prices it) and saves are tracked there, so
 * both outlive the cell: a virtualized row that scrolls away mid-save remounts
 * showing the in-flight value, still locked. Without a provider the state is
 * the cell's own.
 */
export function useLineQuantityDraft(
  suggestionId: string,
  savingHere: boolean,
) {
  const shared = useContext(QuantityDraftContext);
  const [draft, setLocalDraft] = useState<string | null>(null);
  const sharedDraft = shared?.drafts.get(suggestionId) ?? null;
  const pendingElsewhere = shared?.pending.has(suggestionId) ?? false;
  return {
    /** The buyer's unsaved edit in this cell, if any. */
    draft,
    /** What the input shows instead of the saved quantity, if anything. */
    shownDraft: draft ?? sharedDraft,
    isSaving: savingHere || pendingElsewhere,
    setDraft: (value: string | null) => {
      setLocalDraft(value);
      shared?.setDraft(suggestionId, value);
    },
    /** Keeps the line pending until `save` settles. */
    track: <T>(
      save: Promise<T>,
      alsoPending: readonly string[] = [],
    ): Promise<T> => {
      if (!shared) return save;
      const ids = [
        suggestionId,
        ...alsoPending.filter((id) => id !== suggestionId),
      ];
      return shared.trackSave(ids, save);
    },
  };
}
